import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

const IMAGES = [
  // General Contracting
  "https://images.unsplash.com/photo-1759745125627-333e78bc1edc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Construction
  "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Electro Mechanical — MNH authentic PCB / control board photo
  "https://customer-assets.emergentagent.com/job_premium-home-decor-13/artifacts/sl5xdsm6_Electro_Mechanical_Assembly_Manufacturing.jpg",
  // Landscaping
  "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Cleaning Services
  "https://images.unsplash.com/photo-1774750264848-d47a68d82122?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Maintenance
  "https://images.unsplash.com/photo-1646662521253-5b9253b1a207?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  // Airport Ground Services
  "https://images.unsplash.com/photo-1765574782103-6d326d2240f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
];

export default function Services() {
  const { t, dir } = useI18n();
  const isRtl = dir === "rtl";

  return (
    <section id="services" className="bg-background" data-testid="services-section">
      <div className="container-page section-y">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-7">
            <span className="overline">{t.services.eyebrow}</span>
            <h2 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1]">
              {t.services.heading1} <br />
              <span className="text-primary">{t.services.heading1Accent}</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-muted-foreground leading-relaxed">
            {t.services.description}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.services.cards.map((s, i) => {
            // Make the 7th (last) card span full width on lg for visual balance
            const isLastOdd = i === t.services.cards.length - 1 && t.services.cards.length % 3 === 1;
            return (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className={`group relative bg-card border border-border overflow-hidden transition-colors hover:border-primary ${isLastOdd ? "md:col-span-2 lg:col-span-3" : ""}`}
              data-testid={`service-card-${i}`}
            >
              <div className={`relative overflow-hidden ${isLastOdd ? "h-80" : "h-64"}`}>
                <img
                  src={IMAGES[i % IMAGES.length]}
                  alt={s.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className={`absolute top-5 text-xs font-semibold tracking-[0.3em] text-white/80 ${isRtl ? "right-5" : "left-5"}`}>
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className={`absolute top-5 h-10 w-10 border border-white/40 flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-colors ${isRtl ? "left-5" : "right-5"}`}>
                  <ArrowUpRight className={`h-4 w-4 text-white ${isRtl ? "scale-x-[-1]" : ""}`} />
                </div>
                <h3 className="absolute bottom-5 left-6 right-6 text-white text-xl sm:text-2xl font-bold">
                  {s.name}
                </h3>
              </div>
              <div className="p-6">
                <p className="text-muted-foreground leading-relaxed text-sm">{s.summary}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.capabilities.map((c, j) => (
                    <span
                      key={j}
                      className="text-xs px-3 py-1 border border-border bg-muted/50 text-foreground/80"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          );
          })}
        </div>
      </div>
    </section>
  );
}
